import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useApi, useAsyncOperation } from '../hooks/useApi';
import { 
  authAPI, 
  userAPI, 
  aiAgentAPI, 
  workflowAPI, 
  serviceAPI, 
  dashboardAPI,
  apiKeyAPI 
} from '../services/api';

const TestPage = () => {
  const { user, isAuthenticated } = useAuth();
  const [testResults, setTestResults] = useState({});
  const { loading, execute } = useAsyncOperation();

  // Test different API endpoints
  const runTest = async (testName, apiCall) => {
    const result = await execute(apiCall);
    setTestResults(prev => ({
      ...prev,
      [testName]: result
    }));
  };

  const tests = [
    {
      name: 'Public Services',
      description: 'Test public services endpoint (no auth required)',
      action: () => runTest('publicServices', serviceAPI.getPublicServices)
    },
    {
      name: 'User Profile',
      description: 'Test authenticated user profile endpoint',
      action: () => runTest('userProfile', userAPI.getProfile),
      requiresAuth: true
    },
    {
      name: 'Dashboard Stats',
      description: 'Test user dashboard statistics',
      action: () => runTest('dashboardStats', dashboardAPI.getUserStats),
      requiresAuth: true
    },
    {
      name: 'AI Agents',
      description: 'Test user AI agents endpoint',
      action: () => runTest('aiAgents', aiAgentAPI.getUserAgents),
      requiresAuth: true
    },
    {
      name: 'Workflows',
      description: 'Test user workflows endpoint',
      action: () => runTest('workflows', workflowAPI.getUserWorkflows),
      requiresAuth: true
    },
    {
      name: 'API Keys',
      description: 'Test user API keys endpoint',
      action: () => runTest('apiKeys', apiKeyAPI.getUserApiKeys),
      requiresAuth: true
    }
  ];

  return (
    <div className="min-h-screen bg-[#0a1116] text-white p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">API Integration Test Page</h1>
          <p className="text-slate-400">Test backend API endpoints and verify integration</p>
          
          {/* Auth Status */}
          <div className="mt-4 p-4 bg-[#111c22] border border-[#233c48] rounded-lg">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${isAuthenticated ? 'bg-green-500' : 'bg-red-500'}`}></div>
              <span className="font-medium">
                Authentication Status: {isAuthenticated ? 'Authenticated' : 'Not Authenticated'}
              </span>
            </div>
            {user && (
              <div className="mt-2 text-sm text-slate-400">
                Logged in as: <span className="text-white font-medium">{user.username}</span> 
                ({user.role})
              </div>
            )}
          </div>
        </div>

        {/* Test Controls */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {tests.map((test) => (
            <div key={test.name} className="bg-[#111c22] border border-[#233c48] rounded-lg p-4">
              <h3 className="font-bold text-white mb-2">{test.name}</h3>
              <p className="text-sm text-slate-400 mb-4">{test.description}</p>
              
              <button
                onClick={test.action}
                disabled={loading || (test.requiresAuth && !isAuthenticated)}
                className={`w-full py-2 px-4 rounded-lg font-medium text-sm transition-colors ${
                  test.requiresAuth && !isAuthenticated
                    ? 'bg-slate-600 text-slate-400 cursor-not-allowed'
                    : loading
                    ? 'bg-[#13a4ec]/50 text-white cursor-not-allowed'
                    : 'bg-[#13a4ec] hover:bg-[#13a4ec]/90 text-white'
                }`}
              >
                {loading ? 'Testing...' : 'Run Test'}
              </button>
              
              {test.requiresAuth && !isAuthenticated && (
                <p className="text-xs text-amber-400 mt-2">Requires authentication</p>
              )}
            </div>
          ))}
        </div>

        {/* Test Results */}
        <div className="space-y-4">
          <h2 className="text-xl font-bold text-white">Test Results</h2>
          
          {Object.keys(testResults).length === 0 ? (
            <div className="bg-[#111c22] border border-[#233c48] rounded-lg p-6 text-center">
              <span className="material-symbols-outlined text-slate-500 text-4xl mb-2">science</span>
              <p className="text-slate-400">No tests run yet. Click a test button above to start.</p>
            </div>
          ) : (
            Object.entries(testResults).map(([testName, result]) => (
              <div key={testName} className="bg-[#111c22] border border-[#233c48] rounded-lg overflow-hidden">
                <div className={`p-4 border-b border-[#233c48] flex items-center gap-3 ${
                  result.success ? 'bg-green-500/10' : 'bg-red-500/10'
                }`}>
                  <div className={`w-3 h-3 rounded-full ${
                    result.success ? 'bg-green-500' : 'bg-red-500'
                  }`}></div>
                  <h3 className="font-bold text-white">{testName}</h3>
                  <span className={`text-xs px-2 py-1 rounded ${
                    result.success 
                      ? 'bg-green-500/20 text-green-400' 
                      : 'bg-red-500/20 text-red-400'
                  }`}>
                    {result.success ? 'SUCCESS' : 'FAILED'}
                  </span>
                </div>
                
                <div className="p-4">
                  {result.success ? (
                    <div>
                      <p className="text-green-400 text-sm mb-3">✓ API call successful</p>
                      <div className="bg-[#0a1116] border border-[#233c48] rounded p-3">
                        <p className="text-xs text-slate-500 mb-2">Response Data:</p>
                        <pre className="text-xs text-slate-300 overflow-x-auto">
                          {JSON.stringify(result.data, null, 2)}
                        </pre>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <p className="text-red-400 text-sm mb-3">✗ API call failed</p>
                      <div className="bg-red-500/5 border border-red-500/20 rounded p-3">
                        <p className="text-xs text-red-300">{result.error}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Backend Status */}
        <div className="mt-8 p-4 bg-[#111c22] border border-[#233c48] rounded-lg">
          <h3 className="font-bold text-white mb-2">Backend Configuration</h3>
          <div className="text-sm text-slate-400 space-y-1">
            <p>Base URL: <span className="text-white font-mono">http://localhost:8080/api</span></p>
            <p>Expected Backend: Spring Boot with H2 Database</p>
            <p>Demo Credentials: admin/admin123, demo/demo123</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestPage;